<script>
  import { createEventDispatcher } from "svelte";
  import { fly } from "svelte/transition";
  import { onMount, onDestroy } from "svelte";
  import { ScrollingDisable, ScrollingEnable } from "../library/util.js";
  import { appSession } from "../stores/app.js";
  import RestPilots from "../components/RestPilots.svelte";
  import RestStripe from "../components/RestStripe.svelte";
  import MyModal from "../components/MyModal.svelte";
  import { DateTime } from "luxon";
  import UpdateCreditCard from "./UpdateCreditCard.svelte";

  const dispatch = createEventDispatcher();
  let restPilots = null;
  let myModal = null;
  let restStripe = null;
  let pilot = null;
  let busy = true;
  let updateCardActive = false;
  let invoices = [];

  onMount(async () => {
    ScrollingDisable();

    try {
      busy = true;

      // load pilot details
      await getPilot();

      // load recent invoices
      invoices = await restStripe.GetInvoices();
      invoices.forEach((invoice) => {
        let created = DateTime.fromISO(invoice.datetime);
        invoice.date = created.toFormat("MM/dd/yyyy");
      });

      busy = false;
    } catch (error) {
      busy = false;

      switch (error) {
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, pilotId: null });
          break;
        case "NetworkError":
          myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        default:
          myModal.showAlert("Error", error);
          break;
      }
    }
  });

  // get pilot details
  async function getPilot() {
    pilot = await restPilots.GetPilots($appSession.pilotId);
    pilot.subscriptionStart = DateTime.fromISO(pilot.stripe_subscription_start.replace(" ", "T"), {
      zone: "UTC",
    });
    pilot.subscriptionEnd = DateTime.fromISO(pilot.stripe_subscription_end.replace(" ", "T"), { zone: "UTC" });
  }

  onDestroy(() => {
    ScrollingEnable();
  });

  async function cancelSubscription() {
    let confirmed = await myModal.showConfirm(
      "Confirm cancellation",
      "<p>Your subscription will be cancelled at the end of the current billing period.</p><p>You may continue to use JumpHub until then.</p>"
    );
    if (confirmed) {
      try {
        busy = true;
        await restStripe.DeleteSubscription();
        await getPilot();

        busy = false;
      } catch (error) {
        busy = false;
        switch (error) {
          case "BadRole":
          case "BadRealm":
            appSession.set({ authorisationToken: null, pilotId: null });
            break;
          case "NetworkError":
            myModal.showAlert(
              "Network error",
              "A network error occurred.<p> Check network connection and try again.</p>"
            );
            break;
          default:
            myModal.showAlert("Error", error);
            break;
        }
      }
    }
  }

  // refresh pilot details
  async function cardDidUpdate() {
    try {
      busy = true;

      // load pilot details
      await getPilot();

      busy = false;
    } catch (error) {
      busy = false;

      switch (error) {
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, pilotId: null });
          break;
        case "NetworkError":
          myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        default:
          myModal.showAlert("Error", error);
          break;
      }
    }
  }
</script>

<div class="overlay-page-wrapper" transition:fly={{ y: 200, duration: 500 }}>
  <div class="overlay-page-header valign-wrapper">
    <div class="container">
      <div class="row">
        <div class="col s10 offset-s1">
          <h5 class="center-align">Subscription</h5>
        </div>
      </div>
    </div>
  </div>

  <div class="overlay-page-body">
    <div class="container">
      {#if !busy}
        <p>
          <b>Current billing period</b>
          <br />
          {pilot.subscriptionStart.toFormat('MM/dd/yyyy')}
          -
          {pilot.subscriptionEnd.toFormat('MM/dd/yyyy')}
          <span style="font-size: 0.8em; margin-right: 5px;">UTC</span>
          {#if pilot.stripe_subscription_status == 'trial'}(free trial){/if}
          <br />
          {#if pilot.stripe_subscription_cancelled}
            <div style="font-size: 0.8em; margin-top: 5px;">
              Your subscription has been cancelled and will finish at the end of the current billing period.
              <br />
              No further credit card charges will be applied.
            </div>
          {:else}
            <a
              href="#!"
              on:click={() => {
                cancelSubscription();
              }}>
              Cancel subscription
            </a>
          {/if}
        </p>

        <p>
          <b>Credit card</b>
          <br />
          {pilot.stripe_card_brand.toUpperCase()}
          ending
          {pilot.stripe_card_last4}
          <br />
          expires
          {pilot.stripe_card_exp_month.toString().padStart(2, '0')}
          /
          {pilot.stripe_card_exp_year}
          <br />
          {#if !pilot.stripe_subscription_cancelled}
            <a
              href="#!"
              on:click={() => {
                updateCardActive = true;
              }}>Update card</a>
          {/if}
        </p>

        <p><b>Recent invoices</b> <br /></p>
        <div class="row">
          <div class="col s12 m8 l6">
            <table class="striped" style="font-size: 0.9em;">
              <tbody>
                {#each invoices as invoice}
                  <tr>
                    <td>{invoice.date}</td>
                    <td>${invoice.total}</td>
                    <td><a href={invoice.url} target="_blank">View</a></td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      {/if}
    </div>

    {#if busy}
      <div
        style="display: flex; justify-content: center; align-items: center; height: 50px; width: 100%; margin-top: 50px;">
        <div class="preloader-wrapper small active">
          <div class="spinner-layer spinner-green-only">
            <div class="circle-clipper left">
              <div class="circle" />
            </div>
            <div class="gap-patch">
              <div class="circle" />
            </div>
            <div class="circle-clipper right">
              <div class="circle" />
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>

  <div class="overlay-page-footer">
    <div class="container">
      <div class="right">
        <button
          class="waves-effect btn blue-grey"
          on:click={() => {
            dispatch('close', { didUpdate: false });
          }}>
          Done
        </button>
      </div>
    </div>
  </div>
</div>

{#if updateCardActive}
  <UpdateCreditCard
    on:close={(evt) => {
      if (evt.detail.didUpdate) cardDidUpdate();
      updateCardActive = false;
    }} />
{/if}

<RestPilots bind:this={restPilots} />
<MyModal bind:this={myModal} />
<RestStripe bind:this={restStripe} />
